import React, { Fragment, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Display,
  Heading1,
  JustifyContent,
  Space,
  TextAlign,
} from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import type { BoxPrices } from '@library/api-pricing'
import { useMarketingPricingConfig } from '@library/api-pricing'
import {
  BoxPriceCalculator,
  useIsBoxPriceCalculatorEnabled,
  useIsTaperedDiscountCurveEnabled,
} from '@library/landing-pages'

import { trackUTMAndPromoCode } from 'actions/tracking'
import { boxPricesClickPeopleNumber, boxPricesClickRecipeNumber } from 'actions/trackingKeys'
import { boxSizes as boxSize, getHeroDetails } from 'routes/BoxPrices/boxPricesConfig'
import { postcodeSlug } from 'routes/Signup/signupConfig'
import { locationQuery } from 'selectors/routing'

import { BoxPricesContent } from './BoxPricesContent'
import { BoxPricesWithAllPortions } from './BoxPricesWithAllPortions/BoxPricesWithAllPortions'
import { boxPricesBoxSizeSelected } from './boxPricesActions'
import {
  getPriceBreakdown,
  getRecipeOptionsFromBoxInfo,
  getSuitability,
  recipeCountToIndex,
  recipeIndexToCount,
} from './helpers/boxPriceCalculator/boxPriceCalculatorAdapters'

const BoxPricesComponent = () => {
  const dispatch = useDispatch()
  const pricingConfig = useMarketingPricingConfig()
  const isTaperedDiscountCurveEnabled = useIsTaperedDiscountCurveEnabled()
  const isBoxPriceCalculatorEnabled = useIsBoxPriceCalculatorEnabled()
  const { pmaxPortions, pmaxRecipes } = useSelector(locationQuery)

  const [pmaxApplied, setPmaxApplied] = useState(false)
  const [activeNumPortion, setActiveNumPortion] = useState(2)
  const [activeRecipes, setActiveRecipes] = useState(boxSize[2].length - 1)

  const numPersonsToBoxDescriptors = pricingConfig?.numPersonsToBoxDescriptors ?? {}
  const rawPricingData: BoxPrices = pricingConfig?.pricingConfig.data ?? {}
  const availableBoxSizes = useMemo(
    () => pricingConfig?.availableBoxSizes ?? [],
    [pricingConfig?.availableBoxSizes],
  )

  // Both arms are given this same handler, so tracking it here emits one identical event for
  // each. The pmax effect sets the selection directly and so stays untracked, which is correct:
  // a preselected box is not a visitor interaction.
  const handleActivePortionSize = (portionSize: number) => {
    dispatch(trackUTMAndPromoCode(boxPricesClickPeopleNumber, { box_size: portionSize }))
    setActiveNumPortion(portionSize)
    setActiveRecipes(boxSize[portionSize].length - 1)
  }

  useEffect(() => {
    if (availableBoxSizes?.length && pmaxPortions && pmaxRecipes && !pmaxApplied) {
      const pmaxPortionsNum = Number(pmaxPortions)
      if (
        availableBoxSizes.includes(pmaxPortionsNum) &&
        boxSize[pmaxPortions]?.includes(pmaxRecipes)
      ) {
        setActiveNumPortion(pmaxPortionsNum)
        setActiveRecipes(boxSize[pmaxPortions].indexOf(pmaxRecipes))
        setPmaxApplied(true)
      }
    }
  }, [pmaxRecipes, pmaxPortions, availableBoxSizes, pmaxApplied])

  // The selection lives here, not in the calculator, so the pmax preselection and the content
  // below the widget cannot disagree with it. Stored as an index; the calculator wants counts.
  const selectedRecipeCount = recipeIndexToCount(activeRecipes)
  const recipeOptions = getRecipeOptionsFromBoxInfo(numPersonsToBoxDescriptors)(activeNumPortion)
  const hasPriceForSelection = Boolean(rawPricingData?.[activeNumPortion]?.[selectedRecipeCount])

  // Control emits this from its own recipe handler. The calculator has no store access, so the
  // treatment's copy is dispatched here with the same key, payload and thunk.
  const handleRecipesChange = (recipes: number) => {
    dispatch(
      trackUTMAndPromoCode(boxPricesClickRecipeNumber, {
        box_size: activeNumPortion,
        number_of_recipes: recipes,
      }),
    )
    setActiveRecipes(recipeCountToIndex(recipes))
  }

  const handleSelectBox = () => {
    dispatch(
      boxPricesBoxSizeSelected({
        numPersons: activeNumPortion,
        slug: postcodeSlug,
        section: 'main',
      }),
    )
  }

  const showCalculator =
    availableBoxSizes.length > 0 && isBoxPriceCalculatorEnabled && hasPriceForSelection

  const priceBreakdown =
    showCalculator &&
    getPriceBreakdown(rawPricingData, isTaperedDiscountCurveEnabled)(
      activeNumPortion,
      selectedRecipeCount,
    )

  return (
    <Fragment>
      <Box data-testid="boxPricesComponentLoaded">
        <Space size={[6, 8]} direction="vertical" />
        <Heading1 textAlign={TextAlign.Center}>{getHeroDetails().header}</Heading1>
        <Space size={[6, 8]} direction="vertical" />
        <Box
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          gap={8}
          data-testid="boxPricesWithAllPortions"
        >
          {showCalculator && priceBreakdown && (
            <BoxPriceCalculator
              peopleOptions={availableBoxSizes}
              recipeOptions={recipeOptions}
              selectedPeople={activeNumPortion}
              selectedRecipes={selectedRecipeCount}
              onPeopleChange={handleActivePortionSize}
              onRecipesChange={handleRecipesChange}
              suitability={getSuitability(activeNumPortion)}
              pricePerPortion={priceBreakdown.pricePerPortion}
              priceBreakdown={priceBreakdown}
              // Not the shared `cta` constant: BoxPricesComparisonSection renders that in both
              // arms, so changing it would alter control's wording too.
              ctaLabel="Continue with this box"
              onSelectBox={handleSelectBox}
            />
          )}
          {availableBoxSizes.length > 0 && !showCalculator && (
            <BoxPricesWithAllPortions
              boxInfo={numPersonsToBoxDescriptors}
              onClickPortionSize={handleActivePortionSize}
              activeNumPortion={activeNumPortion}
              portionSizes={availableBoxSizes as unknown as string[]}
              activeRecipes={activeRecipes}
              setActiveRecipes={setActiveRecipes}
              isTaperedDiscountCurveEnabled={isTaperedDiscountCurveEnabled}
            />
          )}
        </Box>
        <Space size={[8, 18]} />
        <BoxPricesContent
          activeNumPortion={activeNumPortion}
          isTaperedDiscountCurveEnabled={isTaperedDiscountCurveEnabled}
        />
      </Box>
    </Fragment>
  )
}

export { BoxPricesComponent }
